import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @typedef {Object} CartRefs
 * @property {HTMLButtonElement} [removeButton] - The remove (trash) button, present in cart context.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * When at minimum quantity, swaps the minus button for the remove (trash) button.
   */
  updateButtonStates() {
    const { minusButton, plusButton, removeButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const atMin = value <= min;

    if (removeButton) {
      minusButton.hidden = atMin;
      removeButton.hidden = !atMin;
    } else {
      minusButton.disabled = atMin;
    }

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Dispatches a quantity update with value 0, which the cart interprets as a line item removal.
   */
  removeItem() {
    const { quantityInput } = this.refs;
    quantityInput.value = '0';
    this.onQuantityChange();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
